// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  keycloakInstance,
  initKeycloakOnce,
  persistTokens,
  clearStoredTokens,
  getStoredTokenClaims,
  hasUsableStoredSession,
} from "../config/keycloak";
import {
  userSyncService,
  clearSyncCache,
  getCachedSyncedUser,
} from "../services/userSyncService";
import env from "../utils/env";

const KeycloakContext = createContext();

export const useKeycloak = () => {
  const context = useContext(KeycloakContext);
  if (!context) {
    throw new Error("useKeycloak must be used within a KeycloakProvider");
  }
  return context;
};

export const KeycloakProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(hasUsableStoredSession);
  const [loading, setLoading] = useState(() => !hasUsableStoredSession());
  const [error, setError] = useState(null);
  const refreshIntervalRef = useRef(null);
  const [syncedUser, setSyncedUser] = useState(() => {
    const claims = getStoredTokenClaims();
    return claims?.sub ? getCachedSyncedUser(claims.sub) : null;
  });
  const [syncLoading, setSyncLoading] = useState(false);
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initKeycloak = async () => {
      try {
        // initKeycloakOnce() guarantees keycloak-js is init()'d exactly once
        const authenticated = await initKeycloakOnce();
        setAuthenticated(authenticated);
        setIsInitialized(true);

        if (authenticated) {
          persistTokens(keycloakInstance);
          setupTokenRefresh();
        } else {
          clearStoredTokens();
        }

        keycloakInstance.onTokenExpired = () => {
          keycloakInstance
            .updateToken(70)
            .then((refreshed) => {
              if (refreshed) {
                console.log("Token refreshed successfully");
              }
            })
            .catch((error) => {
              console.error("Failed to refresh token:", error);
              clearStoredTokens();
              setAuthenticated(false);
            });
        };

        keycloakInstance.onAuthRefreshSuccess = () => {
          persistTokens(keycloakInstance);
        };

        keycloakInstance.onAuthLogout = () => {
          clearStoredTokens();
          setAuthenticated(false);
          if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
          }
        };

        keycloakInstance.onAuthSuccess = () => {
          setAuthenticated(true);
          persistTokens(keycloakInstance);
          setupTokenRefresh();
        };

        setLoading(false);
      } catch (error) {
        console.error("Failed to initialize Keycloak:", error);
        clearStoredTokens();
        setError(error);
        setLoading(false);
        setAuthenticated(false);
        setIsInitialized(false);
      }
    };

    const setupTokenRefresh = () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }

      refreshIntervalRef.current = setInterval(
        async () => {
          try {
            const refreshed = await keycloakInstance.updateToken(70);
            if (refreshed) {
              console.log("Token refreshed proactively");
            }
          } catch (error) {
            console.error("Failed to refresh token:", error);
            if (error && error.message !== "Network Error") {
              setAuthenticated(false);
            }
          }
        },
        4 * 60 * 1000,
      );
    };

    initKeycloak();

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, []);

  const login = async () => {
    setError(null);
    console.log("keycloakInstance in login:", keycloakInstance);
    if (!keycloakInstance) {
      console.error("Keycloak instance is not available");
      setError(new Error("Keycloak instance is not available"));
      return;
    }

    // Ensure Keycloak is initialized before calling login
    if (!isInitialized && keycloakInstance.authenticated === undefined) {
      try {
        await initKeycloakOnce();
        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to initialize Keycloak before login:", error);
        setError(error);
        return;
      }
    }

    try {
      if (keycloakInstance.login) {
        // No saved-path redirect: after login the user lands on their own
        // default page (RouteRenderer → getFirstAccessibleRoute), never the
        // previous session's page.
        keycloakInstance.login();
      } else {
        throw new Error("Keycloak login method is not available");
      }
    } catch (error) {
      console.error("Failed to call login:", error);
      setError(error);
    }
  };

  const logout = async () => {
    setError(null);
    if (!keycloakInstance) {
      console.error("Keycloak instance is not available");
      setError(new Error("Keycloak instance is not available"));
      return;
    }

    // Ensure Keycloak is initialized before calling logout
    if (!isInitialized && keycloakInstance.authenticated === undefined) {
      try {
        await initKeycloakOnce();
        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to initialize Keycloak before logout:", error);
        setError(error);
        return;
      }
    }

    try {
      // Clear all caches
      sessionStorage.removeItem("redirectAfterLogin");
      clearStoredTokens(); // Drop persisted Keycloak tokens
      clearSyncCache(); // Clear user sync cache

      // Clear permission caches
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith("user_permissions_")) {
          sessionStorage.removeItem(key);
        }
      });

      // Reset synced user state
      setSyncedUser(null);

      if (keycloakInstance.logout) {
        keycloakInstance.logout({ redirectUri: window.location.origin });
      } else {
        throw new Error("Keycloak logout method is not available");
      }
    } catch (error) {
      console.error("Failed to call logout:", error);
      setError(error);
    }
  };

  const register = async () => {
    setError(null);
    if (!keycloakInstance) {
      console.error("Keycloak instance is not available");
      setError(new Error("Keycloak instance is not available"));
      return;
    }

    // Ensure Keycloak is initialized before calling register
    if (!isInitialized && keycloakInstance.authenticated === undefined) {
      try {
        await initKeycloakOnce();
        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to initialize Keycloak before register:", error);
        setError(error);
        return;
      }
    }

    try {
      if (keycloakInstance.register) {
        keycloakInstance.register();
      } else {
        throw new Error("Keycloak register method is not available");
      }
    } catch (error) {
      console.error("Failed to call register:", error);
      setError(error);
    }
  };

  const hasRole = (role) => {
    const resourceName = env.REACT_APP_KEYCLOAK_CLIENT_ID;
    if (keycloakInstance.tokenParsed) {
      return (
        keycloakInstance.hasResourceRole(role, resourceName) ||
        keycloakInstance.hasRealmRole(role)
      );
    }
    const claims = getStoredTokenClaims();
    const resourceRoles = claims?.resource_access?.[resourceName]?.roles || [];
    const realmRoles = claims?.realm_access?.roles || [];
    return resourceRoles.includes(role) || realmRoles.includes(role);
  };

  const hasResourceRole = (role, resource) => {
    return keycloakInstance.hasResourceRole(role, resource);
  };

  const getToken = () => {
    return keycloakInstance.token;
  };

  const syncUserWithDatabase = async (userInfo) => {
    try {
      setSyncLoading(true);
      const syncedUserData = await userSyncService.syncUser(userInfo);

      if (syncedUserData) {
        setSyncedUser(syncedUserData);
        return syncedUserData;
      } else {
        throw new Error("User sync failed - returned null");
      }
    } catch (error) {
      console.error("Error syncing user:", error);
      throw error;
    } finally {
      setSyncLoading(false);
    }
  };

  const getUserInfo = () => {
    const claims = keycloakInstance.tokenParsed || getStoredTokenClaims();
    if (!claims) return null;

    const keycloakInfo = {
      id: claims.sub,
      username: claims.preferred_username,
      email: claims.email,
      firstName: claims.given_name,
      lastName: claims.family_name,
      roles: claims.realm_access?.roles || [],
      resourceRoles: claims.resource_access || {},
    };

    if (syncedUser) {
      return {
        ...keycloakInfo,
        ...syncedUser,
        roles: syncedUser.roles || keycloakInfo.roles,
        resourceRoles: keycloakInfo.resourceRoles,
      };
    }

    return keycloakInfo;
  };

  const getAllUserRoles = () => {
    const claims = keycloakInstance.tokenParsed || getStoredTokenClaims();
    if (!claims) return { realmRoles: [], resourceRoles: {} };

    return {
      realmRoles: claims.realm_access?.roles || [],
      resourceRoles: claims.resource_access || {},
    };
  };

  const clearError = () => {
    setError(null);
  };

  useEffect(() => {
    if (authenticated) {
      setCurrentUserInfo(getUserInfo());
    } else {
      setCurrentUserInfo(null);
    }
  }, [authenticated, keycloakInstance.tokenParsed, syncedUser]);

  const value = {
    authenticated,
    keycloakInstance,
    loading,
    error,
    login,
    logout,
    register,
    hasRole,
    hasResourceRole,
    getToken,
    getUserInfo,
    getAllUserRoles,
    clearError,
    syncUserWithDatabase,
    syncedUser,
    syncLoading,
    userInfo: currentUserInfo,
  };

  return (
    <KeycloakContext.Provider value={value}>
      {children}
    </KeycloakContext.Provider>
  );
};
